import { PromoToggleHelper } from '@/scripts/libs/promo-toggles'
import { PromoToggle } from '@/types/plp'
import {
  ALL_PROMOS,
  ALL_PROMOS_LABEL,
  FLASH,
  PST,
  PST_PROMOS,
  SCARCITY,
  SCARCITY_PROMO,
} from '@/scripts/constants/promos'
import AlgoliaPlp from '@/scripts/libs/algolia/plp'
import { AlgoliaProductHit } from '@/scripts/libs/algolia/types/plp'
import { SearchResponse } from '@/scripts/libs/algolia'
import { WcsUser } from '@/scripts/libs/wcs/user'
import wcs from '@/scripts/libs/wcs'
import axios from 'axios'
import { WcsEspotResponse } from '@/scripts/libs/wcs/types/wcs-config'
import { WcsPromotions } from '@/scripts/libs/wcs/promotions'
import makeDebug from 'debug'
const debug = makeDebug('Algolia:PromoToggles')

export class AlgoliaPromoToggles implements PromoToggleHelper {
  public readonly PLPPromoConfigurationEspot: undefined | Promise<any>

  constructor() {
    if (wcs.isInPlp() || wcs.isInSearch()) {
      this.PLPPromoConfigurationEspot = new Promise((resolve) => {
        if (this.PLPPromoConfigurationEspot) {
          resolve(this.PLPPromoConfigurationEspot)
          return
        }

        this.fetchEspot()
          .then((res) => {
            resolve(res)
          })
          .catch(() => {
            debug('PLPPromoConfigurationEspot call error')
            resolve(undefined)
          })
      })
    }
  }

  getPromoToggles = async (sh?: SearchResponse<AlgoliaProductHit>): Promise<Array<PromoToggle>> => {
    const searchResponse = sh ?? (await AlgoliaPlp.getPromoToggles())
    const promoToggles = this.mapPromoToggles(searchResponse)
    const espot = await this.PLPPromoConfigurationEspot
    const matchedPromoToggles = this.matchCustomerSegmentVisibility(promoToggles, espot)
    const filteredPromoToggles = this.filterPromoToggles(matchedPromoToggles)
    const sortedPromoToggles = this.sortPromoTogglesByEspotOrder(filteredPromoToggles, espot)
    return sortedPromoToggles
  }

  private sortPromoTogglesByEspotOrder(promoToggles: Array<PromoToggle>, espot: any) {
    if (!espot || !espot?.PSTFilter) {
      return promoToggles
    }

    const orderFromEspot = espot.PSTFilter.map(
      (filter: { name: string; visibility: string }) => filter.name
    )
    const sortedToggles = [...promoToggles].sort((a, b) => {
      if (a.id === ALL_PROMOS || b.id === ALL_PROMOS) {
        return -1
      }

      const includesA = orderFromEspot.some((name: string) => a.command?.includes(name))
      const includesB = orderFromEspot.some((name: string) => b.command?.includes(name))

      if (includesA && includesB) {
        const indexA = orderFromEspot.findIndex((name: string) => a.command?.includes(name))
        const indexB = orderFromEspot.findIndex((name: string) => b.command?.includes(name))
        return indexA - indexB
      }

      if (includesA) {
        return 1
      }

      if (includesB) {
        return -1
      }

      return 0
    })

    return sortedToggles
  }

  mapPromoToggles(
    searchResponse: undefined | SearchResponse<AlgoliaProductHit>
  ): Array<PromoToggle> {
    if (!searchResponse || !searchResponse.facets) return []

    const badges = Object.entries(searchResponse.facets).filter(
      ([key]) => key.includes('priceFilter') && key.includes('.badge')
    )

    if (!badges.length) return []

    const mappedToggles = badges.map(([key, value], index) => {
      const [segmentsAndId, startDate, endDate] = key
        .replace('.badge', '')
        .replace('priceFilter_', '')
        .split('|')
      const [badge] = Object.keys(value)
      const [count] = Object.values(value)
      const [segments, id] = segmentsAndId.split('.')
      return this.mapPromoToggle({
        id,
        index,
        startDate,
        endDate,
        badge,
        fullId: key,
        segments,
        count,
      })
    })
    const isUserEligibleForEarlyAccess = WcsUser.isEarlyAccessActivityInUserClusterCookie()
    const earlyAccessIndex = mappedToggles.findIndex((pm) => pm.command?.includes('_EA_'))
    if (!isUserEligibleForEarlyAccess && earlyAccessIndex !== -1) {
      mappedToggles.splice(earlyAccessIndex, 1)
    }

    if (mappedToggles.length === 0) {
      return []
    }

    const allPromoToggleVisibility = this.displayAllPromosToggleIsVisible('ALL')
    if (allPromoToggleVisibility) {
      return [
        {
          id: ALL_PROMOS,
          badge: ALL_PROMOS_LABEL,
          dataElID: 'X_SunPLP_0-Filter_AllPromos',
          isFlash: false,
          entryValue: mappedToggles.map((toggle) => toggle.entryValue).flat(),
          icon: ALL_PROMOS,
          name: ALL_PROMOS_LABEL,
          endDate: '',
          endDateTimestamp: 0,
          startDate: '',
          startDateTimestamp: 0,
          type: '',
          userSegments: '',
        },
        ...mappedToggles,
      ]
    }

    return mappedToggles
  }

  /* TODO: Extract this in abstract class */
  private displayAllPromosToggleIsVisible = (displayAllPromosToggle: string) => {
    if (displayAllPromosToggle === 'ALL') {
      return true
    }
    if (displayAllPromosToggle === 'NONE') {
      return false
    } else {
      const userCluster = WcsUser.getMergedUserCluster()
      return (
        displayAllPromosToggle &&
        userCluster?.some((cluster) => displayAllPromosToggle?.includes(cluster))
      )
    }
  }

  private mapPromoToggle = (promo: {
    badge: string
    id: string
    index: number
    fullId: string
    startDate: string
    endDate: string
    segments: string
    count?: number
  }): PromoToggle => {
    const { badge, id, index, endDate, startDate, fullId, segments, count } = promo

    const [unformattedStartDate, unformattedStartHour] = startDate.split(' ')
    const formattedStartDate = `${unformattedStartDate} ${unformattedStartHour
      .replaceAll('-', ':')
      .substring(0, unformattedStartHour.length)}`

    const [unformattedEndDate, unformattedEndHour] = endDate.split(' ')
    const formattedEndDate = `${unformattedEndDate} ${unformattedEndHour
      .replaceAll('-', ':')
      .substring(0, unformattedEndHour.length)}`

    let promoId = ''
    let type = ''
    let icon = ''
    if (id.toLowerCase().includes(PST)) {
      promoId = `${PST_PROMOS}_${index}`
      type = PST
      icon = PST_PROMOS
    } else if (id.toLowerCase().includes(FLASH)) {
      promoId = `${SCARCITY_PROMO}_${index}`
      type = SCARCITY
      icon = SCARCITY_PROMO
    }

    const userSegments = segments.includes('Guest') ? '' : segments
    /* TODO: Extract sanitizedDate from WCS side for coherency */
    const sanitizedStartDate = WcsPromotions.sanitizeDate(`${formattedStartDate} CDT`)
    const sanitizedEndDate = WcsPromotions.sanitizeDate(`${formattedEndDate} CDT`)

    return {
      id: promoId,
      badge,
      dataElID: '',
      command: id,
      entryValue: [`${fullId}:${badge}`.replace(/:(-)/g, ':\\$1')],
      icon,
      isFlash: type === SCARCITY,
      name: badge,
      startDate: sanitizedStartDate,
      startDateTimestamp: new Date(sanitizedStartDate).getTime(),
      endDate: sanitizedEndDate,
      endDateTimestamp: new Date(sanitizedEndDate).getTime(),
      type,
      userSegments,
      count,
    }
  }
  sortPromotions(originalOrder: string[], promoToggles: PromoToggle[]): PromoToggle[] {
    const sortedPromotions = promoToggles.sort((currentPromo, nextPromo) => {
      if (nextPromo.id === ALL_PROMOS) {
        return 1
      }

      if (currentPromo.id === ALL_PROMOS) {
        return -1
      }
      const currentEntryValue = `${currentPromo.entryValue[0]}`
      const nextEntryValue = `${nextPromo.entryValue[0]}`
      return originalOrder.indexOf(currentEntryValue) - originalOrder.indexOf(nextEntryValue)
    })
    return sortedPromotions
  }

  private async fetchEspot() {
    const { storeId, langId } = wcs.getStoreConfig()
    const endpoint = `${window.origin}/wcs/resources/store/${storeId}/espot/PLP_PROMO_CONFIGURATION?langId=${langId}`
    const { data }: { data: WcsEspotResponse<string> } = await axios.get(endpoint)

    const content = JSON.parse(
      data?.MarketingSpotData[0]?.baseMarketingSpotActivityData[0]?.marketingContentDescription[0]
        ?.marketingText
    )
    const category = (document.getElementById('pageType') as HTMLInputElement)?.value

    const categoryContent = content[category] ?? []
    return categoryContent
  }

  private getAllPromosToggleVisibility(PSTFilter: any) {
    const allPromosFilter = PSTFilter?.find(
      (filter: string | { name: string; visibility: string }) => {
        if (typeof filter !== 'string') {
          return filter.name === 'All Promo'
        }
      }
    )

    const allPromosTogglesVisibility = allPromosFilter ? allPromosFilter.visibility : 'ALL'
    return allPromosTogglesVisibility as string
  }

  /* TODO: Extract this in abstract class */
  private filterPromoToggles = (promotions: PromoToggle[]) => {
    return promotions.filter((promo) => {
      if (!promo.customerSegmentVisibility || promo.customerSegmentVisibility === 'ALL') {
        return promo
      }
      if (promo.customerSegmentVisibility !== 'NONE') {
        const userCluster = WcsUser.getMergedUserCluster()
        return (
          !promo?.customerSegmentVisibility ||
          promo.customerSegmentVisibility.length === 0 ||
          userCluster?.some((cluster) => promo?.customerSegmentVisibility?.includes(cluster))
        )
      }
    })
  }

  private matchCustomerSegmentVisibility(promoToggles: Array<PromoToggle>, espot: any) {
    const matchedPromoToggles = promoToggles.map((promoToggle) => {
      if (promoToggle.id === ALL_PROMOS) {
        const allPromosToggleVisibility = this.getAllPromosToggleVisibility(espot?.PSTFilter)

        return {
          ...promoToggle,
          customerSegmentVisibility: allPromosToggleVisibility,
        }
      }

      const match = espot?.PSTFilter?.find(
        (filter: string | { name: string; visibility: string }) => {
          if (typeof filter === 'string') {
            return promoToggle?.command?.includes(filter)
          }
          return promoToggle?.command?.includes(filter.name)
        }
      )

      if (!match) {
        return {
          ...promoToggle,
          customerSegmentVisibility: this.displayAllPromosToggleIsVisible('ALL') ? 'ALL' : 'NONE',
        }
      }

      return {
        ...promoToggle,
        customerSegmentVisibility: match.visibility ?? 'ALL',
      }
    })
    return matchedPromoToggles
  }

  public static generateModule() {
    if (window.algoliaPromoToggles) {
      return window.algoliaPromoToggles
    }

    return new AlgoliaPromoToggles()
  }
}

window.algoliaPromoToggles = AlgoliaPromoToggles.generateModule()

export default window.algoliaPromoToggles
